import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Paper, Chip } from "@mui/material";
import { withRouter } from "react-router";
import { observer } from "mobx-react-lite";
import StyledPage from "../../common/StyledPage";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { getSiteContentArticle } from "../../../actions/internal/SiteActions";
import { getSiteContentByTrackingId } from "../../../selectors/SiteSelectors";
import { getUserId } from "../../../selectors/UserSelectors";
import ReactMarkdown from 'react-markdown';
import StyledStatusChip from "../../common/StyledStatusChip";
import StyledSection from "../../common/StyledSection";
import { ContainedButton, LoadingButton } from "../../common/StyledButtons";
import { GENERATION_STATUS } from "../../../constants/Statuses";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import ConfirmationModal from "../modals/ConfirmationModal";

const ViewSiteContentDetailsPage = ({ match }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);

  const siteId = match.params.site_id;
  const trackingId = match.params.article_id;
  const article = getSiteContentByTrackingId(trackingId);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getSiteContentArticle(getUserId(), siteId, trackingId);
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = () => {
    try {
      setIsPublishing(true);

    } catch(ex) {
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Failed to publish this article, please try again later.');
    } finally {
      setIsPublishing(false);
    }
  }

  const renderLoading = () => <StyledProgressCircle />;

  const renderDescription = (text) =>
    text.split("\n").reduce((acc, val, i) => [...acc, <div key={i}>{val}</div>], []);

  const renderArticleCard = () => (
    <StyledSection
    title="Article"
    rightAdornment={
      <StyledStatusChip status={article.generator_status} />
    }>
      <h5 style={{ marginBottom: 8 }}>Actions</h5>
      <Box sx={{ gap: '8px', display: 'flex', flexDirection: 'row', paddingBottom: '8px' }}>
        {article.generator_status === GENERATION_STATUS.SUCCESS &&
            <LoadingButton onClick={() => setShowConfirmPublish(true)} isLoading={isPublishing}>
              Approve & Publish
            </LoadingButton>
        }
        {article.generator_status === GENERATION_STATUS.SUCCESS &&
          <ContainedButton>
            Edit
          </ContainedButton>
        } 
        {article.generator_status === GENERATION_STATUS.PUBLISHED &&
          <ContainedButton>
            Hide
          </ContainedButton>
        }
      </Box>

      <Divider />
      <Box sx={{ marginTop: '24px' }}>
            <h5 style={{ marginBottom: 8 }}>Full Article</h5>

        <Box
            sx={{
            border: '1px solid #ddd',
            borderRadius: '12px',
            padding: '24px 48px',
            backgroundColor: '#fff',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
            }}
        >
            <Box
            className="markdown-content"
            sx={{
                '& h1': { fontSize: '1.9rem', fontWeight: 600, mt: 2, mb: 1 },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1, textTransform: 'none' },
                '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.8, textTransform: 'none' },
                '& h4': { fontSize: '1rem', fontWeight: 500, mt: 1.2, mb: 0.6, textTransform: 'none' },
                '& h5': { fontSize: '0.9rem', fontWeight: 500, mt: 1, mb: 0.5, textTransform: 'none' },
                '& p': { fontSize: '0.95rem', lineHeight: 1.6, mb: 1, textTransform: 'none' },
                '& ul, & ol': { fontSize: '0.95rem', ml: 3 },
                '& code': {
                fontSize: '0.85rem',
                backgroundColor: '#f5f5f5',
                px: 0.5,
                py: 0.2,
                borderRadius: '4px',
                fontFamily: 'monospace',
                },
            }}
            >
            <ReactMarkdown>{(article && article.content.final_draft.draft_article) || ''}</ReactMarkdown>
            </Box>
        </Box>
      </Box>
    </StyledSection>
  );

  const renderDetailsCard = () => (
    <StyledSection
      title="Article Details"
    >
      <Box sx={{ marginTop: "12px" }}>
        <h5 style={{ marginBottom: 0 }}>Brief</h5>
        <p>{renderDescription(article.content.brief || "No summary provided.")}</p>
      </Box>
      <Divider sx={{ marginY: 2 }} />
    </StyledSection>
  );

  return (
    <StyledPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledSubHeader title="Article Details" />
        </Grid>
        <Grid item xs={12}>
          {isLoading ? renderLoading() : 
          <>
            {renderArticleCard()}
            {renderDetailsCard()}
          </>
          
          
          }
        </Grid>
      </Grid>
      <ConfirmationModal
        open={showConfirmPublish}
        onCancel={() => setShowConfirmPublish(false)}
        description={"Please confirm that you want to publish this article. Once this action has taken place this content will be publically accessible."}
        okButtonText='Publish'
        cancelButtonText='Cancel'
        onOk={() => {
          setShowConfirmPublish(false);
          handlePublish();
        }}
      />
    </StyledPage>
  );
};

export default observer(withRouter(ViewSiteContentDetailsPage));
